import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { SectionHeader } from "@/components/common/Widgets";
import { Upload, Download, CheckCircle2, XCircle, FileSpreadsheet, FileText, AlertTriangle, Loader2 } from "lucide-react";

function DownloadButton({ format, label }) {
  const onClick = async () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("acas_token");
    try {
      const r = await fetch(`${BACKEND_URL}/api/customers/bulk/template?format=${format}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("download");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `customers_template.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };
  return (
    <Button variant="outline" size="sm" onClick={onClick} data-testid={`download-template-${format}`}>
      <Download className="h-3.5 w-3.5 mr-1" />{label}
    </Button>
  );
}

export default function CustomersBulkUploadPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [skipInvalid, setSkipInvalid] = useState(true);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(null); setImportResult(null);
  };

  const doPreview = async () => {
    if (!file) return toast.error("Choose a file first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/customers/bulk/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      toast.success(`${r.data.valid_count} valid · ${r.data.invalid_count} invalid`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const doImport = async () => {
    if (!file) return toast.error("Choose a file first");
    if (!window.confirm(`Import ${preview?.valid_count || 0} customers? ${preview?.invalid_count ? `(${preview.invalid_count} invalid rows will be skipped)` : ""}`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("skip_invalid", skipInvalid);
      const r = await api.post("/customers/bulk/import?skip_invalid=" + skipInvalid, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(r.data);
      toast.success(`${r.data.inserted_count} customers imported`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const reset = () => { setFile(null); setPreview(null); setImportResult(null); if (fileRef.current) fileRef.current.value = ""; };

  const downloadErrorReport = (source) => {
    const rows = source === "preview" ? (preview?.invalid || []) : (importResult?.skipped || []);
    if (!rows.length) return toast.error("No errors to export");
    // Collect all keys from any row.data + errors column
    const allKeys = new Set(["row", "errors"]);
    rows.forEach((r) => Object.keys(r.data || {}).forEach((k) => allKeys.add(k)));
    const headers = Array.from(allKeys);
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      const flat = { row: r.row, errors: (r.errors || []).join("; "), ...(r.data || {}) };
      lines.push(headers.map((h) => escape(flat[h])).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bulk_errors_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} error row(s) exported`);
  };

  return (
    <div className="space-y-5">
      <SectionHeader
        title="Bulk Customer Upload"
        subtitle="Import existing customers from a CSV or XLSX file. Download the template, fill it in, then upload."
        action={(
          <Button variant="ghost" onClick={() => navigate("/customers")}>← Back to Customers</Button>
        )}
      />

      <Card className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Step 1 — Download Template</Label>
              <p className="text-sm mt-1 mb-2 text-muted-foreground">Use this template to format your data correctly. Required columns: <code className="mono text-[11px]">first_name</code>, <code className="mono text-[11px]">last_name</code>, <code className="mono text-[11px]">constitution</code>.</p>
              <div className="flex gap-2">
                <DownloadButton format="csv" label="CSV Template" />
                <DownloadButton format="xlsx" label="Excel Template" />
              </div>
            </div>
            <div className="pt-3 border-t">
              <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Step 2 — Upload Your File</Label>
              <div className="mt-2 rounded-md border border-dashed border-border bg-muted/30 p-5 text-center">
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onPick} className="hidden" />
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="bulk-pick-file">
                  <Upload className="h-4 w-4 mr-1.5" /> Choose CSV / XLSX file
                </Button>
                {file && <div className="mt-2 text-sm flex items-center justify-center gap-2"><FileSpreadsheet className="h-4 w-4 text-accent" /><span className="font-medium">{file.name}</span><Badge variant="outline" className="text-[10px]">{(file.size / 1024).toFixed(1)} KB</Badge></div>}
              </div>
            </div>
            <div className="pt-3 border-t flex flex-wrap items-center gap-3">
              <Button onClick={doPreview} disabled={!file || busy} data-testid="bulk-preview-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />}Preview
              </Button>
              <Button onClick={doImport} disabled={!preview || preview.valid_count === 0 || busy} data-testid="bulk-import-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}Import {preview?.valid_count ? `(${preview.valid_count})` : ""}
              </Button>
              <div className="flex items-center gap-2 ml-2"><Switch checked={skipInvalid} onCheckedChange={setSkipInvalid} id="skip-invalid" /><Label htmlFor="skip-invalid" className="text-xs">Skip invalid rows (recommended)</Label></div>
              {(preview || importResult) && <Button variant="ghost" onClick={reset}>Reset</Button>}
            </div>
          </div>

          <Card className="p-4 bg-muted/30 border-dashed text-xs space-y-2">
            <div className="font-semibold text-foreground heading text-sm">Column reference</div>
            <ul className="space-y-1 text-muted-foreground">
              <li><span className="text-foreground font-medium">Required:</span> first_name, last_name, constitution</li>
              <li><span className="text-foreground font-medium">Recommended:</span> mobile, pan, branch, dob, occupation</li>
              <li><span className="text-foreground font-medium">Optional:</span> middle_name, email, address, gender, marital_status, gstin, activity, promoter_name, cibil_score, experian_score, equifax_score, crif_score</li>
            </ul>
            <div className="pt-2 border-t text-[11px] text-muted-foreground">
              <div>• Constitution must be: <code className="mono">Individual, Proprietorship, Partnership, LLP, Pvt Ltd, Public Ltd, Trust, HUF, Co-operative Society</code></div>
              <div>• PAN must be 10-char (5 letters + 4 digits + 1 letter)</div>
              <div>• Dates in YYYY-MM-DD format</div>
              <div>• PII fields (mobile, PAN, email) are AES-encrypted at rest</div>
            </div>
          </Card>
        </div>
      </Card>

      {preview && (
        <Card className="p-0 overflow-hidden">
          <div className="px-5 py-3 border-b flex items-center justify-between bg-muted/30">
            <div className="flex items-center gap-3">
              <h3 className="font-semibold heading text-sm">Preview · {preview.file_name}</h3>
              <Badge variant="outline" className="text-success border-success/40"><CheckCircle2 className="h-3 w-3 mr-1 inline" />{preview.valid_count} valid</Badge>
              {preview.invalid_count > 0 && <Badge variant="outline" className="text-destructive border-destructive/40"><XCircle className="h-3 w-3 mr-1 inline" />{preview.invalid_count} invalid</Badge>}
              <span className="text-[11px] text-muted-foreground">Total parsed: {preview.total}</span>
            </div>
          </div>
          {preview.invalid_count > 0 && (
            <div className="px-5 py-3 border-b">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2 text-warning"><AlertTriangle className="h-4 w-4" /><span className="font-semibold text-sm">Invalid rows ({preview.invalid_count})</span></div>
                <Button size="sm" variant="outline" onClick={() => downloadErrorReport("preview")} data-testid="bulk-download-preview-errors">
                  <Download className="h-3.5 w-3.5 mr-1" /> Download Error Report (CSV)
                </Button>
              </div>
              <div className="max-h-40 overflow-y-auto rounded-md border border-border bg-muted/20 text-xs">
                <table className="w-full">
                  <thead className="bg-muted/40 text-[10px] uppercase">
                    <tr><th className="text-left px-2 py-1.5">Row</th><th className="text-left px-2 py-1.5">Errors</th><th className="text-left px-2 py-1.5">Preview</th></tr>
                  </thead>
                  <tbody>
                    {preview.invalid.map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-2 py-1.5 mono">{r.row}</td>
                        <td className="px-2 py-1.5 text-destructive">{r.errors.join(", ")}</td>
                        <td className="px-2 py-1.5 text-muted-foreground">{r.data?.first_name} {r.data?.last_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {preview.valid.length > 0 && (
            <div className="px-5 py-3">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Valid rows preview (first 20)</div>
              <div className="max-h-72 overflow-y-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 text-[10px] uppercase">
                    <tr>
                      <th className="text-left px-2 py-1.5">Row</th>
                      <th className="text-left px-2 py-1.5">Name</th>
                      <th className="text-left px-2 py-1.5">Constitution</th>
                      <th className="text-left px-2 py-1.5">Branch</th>
                      <th className="text-left px-2 py-1.5">Mobile</th>
                      <th className="text-left px-2 py-1.5">PAN</th>
                      <th className="text-left px-2 py-1.5">CIBIL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.valid.slice(0, 20).map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-2 py-1.5 mono">{r.row}</td>
                        <td className="px-2 py-1.5 font-medium">{[r.data.first_name, r.data.middle_name, r.data.last_name].filter(Boolean).join(" ")}</td>
                        <td className="px-2 py-1.5"><Badge variant="secondary" className="text-[10px]">{r.data.constitution}</Badge></td>
                        <td className="px-2 py-1.5">{r.data.branch || "—"}</td>
                        <td className="px-2 py-1.5 mono">{r.data.mobile || "—"}</td>
                        <td className="px-2 py-1.5 mono">{r.data.pan || "—"}</td>
                        <td className="px-2 py-1.5 mono">{r.data.cibil_score || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      )}

      {importResult && (
        <Card className="p-5 border-success/30 bg-success/5">
          <div className="flex items-center gap-2 mb-3"><CheckCircle2 className="h-5 w-5 text-success" /><h3 className="font-semibold heading text-base">Import complete</h3></div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center"><div className="text-2xl font-semibold heading">{importResult.total}</div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Parsed</div></div>
            <div className="text-center text-success"><div className="text-2xl font-semibold heading">{importResult.inserted_count}</div><div className="text-[11px] uppercase tracking-wider">Inserted</div></div>
            <div className="text-center text-destructive"><div className="text-2xl font-semibold heading">{importResult.skipped_count}</div><div className="text-[11px] uppercase tracking-wider">Skipped</div></div>
          </div>
          {importResult.inserted_count > 0 && (
            <Button onClick={() => navigate("/customers")} data-testid="bulk-goto-customers">View All Customers →</Button>
          )}
          {importResult.skipped_count > 0 && (
            <Button variant="outline" onClick={() => downloadErrorReport("import")} className="ml-2" data-testid="bulk-download-import-errors">
              <Download className="h-3.5 w-3.5 mr-1" /> Download Skipped Rows ({importResult.skipped_count})
            </Button>
          )}
        </Card>
      )}
    </div>
  );
}
